import { Frame } from 'scenejs';
import shortid from "shortid";

import _ from 'lodash'

import { 
  CONTENT, CONTENT_CARD, FORM, FORM_FIELD, FORM_STEP, PAGE, SECTION, SECTION_COLUMN, 
  MENU, EXTERNAL_FORM, HOTSPOT, CAROUSEL, CAROUSEL_CARD, ASSET, SEEK, LIVE_ELEMENT_CONTAINER, 
  MODAL, SLIDE, GROUP, ANCHOR, 
  TEXT,
  MENU_CONTAINER,
  CONTENT_CARD_TEMPLATE,
  MENU_DRAGGABLE_CONTAINER,
  LIVE_ELEMENT,
  FORM_DROPDOWN_LIST,
  FORM_SUBMIT,
  FORM_CHECK_LIST,
  FORM_CHECK_BOX,
  OTP_FORM_SUBMIT,
  FORM_NUMBER,
  FORM_TEXTAREA,
  OTP_INPUT,
  OTP_RESEND,
  IMAGE,
  } from "constants/widgetTypes";
import { WidgetRegistry } from "../widgets/registry";

const formTypes = [
  FORM,
  FORM_STEP,
  FORM_FIELD,
  FORM_NUMBER,
  FORM_TEXTAREA,
  FORM_DROPDOWN_LIST,
  FORM_CHECK_LIST,
  FORM_CHECK_BOX,
  FORM_SUBMIT,
  OTP_INPUT,
  OTP_FORM_SUBMIT,
  OTP_RESEND,
  EXTERNAL_FORM
];

/**
 * Generate Widget request for a Desktop
 */
export function generateWidgetRequestForDesktop(editorSession, widgets) {
  let widgetRequests = widgets.map((widget, index) => {

    let rect = getWidgetBoundingRectForBreakpoint(editorSession, widget, 'desktop');
    let rotation = _.get(widget, `properties.mobile.layout.transform.rotate`) || 0;
    rotation = parseRotationValue(rotation);

    if (!rect) {
      return null;
    }

    let request = {
      id: widget.uid,
      type: widget.type,
      x: rect.x,
      y: rect.y,
      rotation: rotation,
      width: rect.width,
      height: rect.height,
      parent: widget.parent,
    }

    return request;
  }).filter(item => item !== null);

  widgetRequests = sortWidgets(widgetRequests);

  return widgetRequests;
}

const parseRotationValue = (value) => {
  value = parseFloat(value);
  value = Math.round(value);

  var processed = value;

  if (value >= 0) {
    const count = Math.floor((value / 360), 0);
    processed = value - 360 * count;
  } else {
    const count = Math.ceil(Math.abs(value / 360));
    processed = 360 * count + value;
  }

  processed = processed || 0;
  return processed;
}

//ANCHOR, HOTSPOT, CONTENT, CONTENT_CARD, CONTENT_CARD_TEMPLATE, FORM, FORM_STEP, MENU, MENU_CONTAINER
const IGNORE_WIDGETS_FOR_PROCESSING = [ANCHOR, HOTSPOT, CONTENT_CARD, CONTENT_CARD_TEMPLATE, MENU_CONTAINER];

/**
 * Calculate dimension for a widget
 */
export function recalculateLayoutForBreakpoint(context, widget) {
  const editorSession = context.editorSession;
  const breakpoint = context.breakpoint;
  context.optimizedWidgets = {};
  context.formDetails = {};
  context.availableWidth = 375;

  // let pageWidgets = context.widgets.filter(item => item.parent === widget.uid && !_.includes([IGNORE_WIDGETS_FOR_PROCESSING], item.type));
  let pageWidgets = context.widgets.filter(item => item.parent === widget.uid && !_.includes(IGNORE_WIDGETS_FOR_PROCESSING, item.type));

  const rootWidgets = generateWidgetRequestForDesktop(editorSession, pageWidgets, context.widgets);

  // Calculate layouts for all widgets, including nested children and root widgets
  const layouts = calculateMobileLayoutForAllWidgets(context, rootWidgets, widget);

  return layouts;
}

function sortWidgets(widgetRequests) {
  return widgetRequests.sort((a, b) => {
    if (a.y === b.y) {
      return a.x - b.x;  // Sort by x if y values are equal
    }
    return a.y - b.y;  // Sort by y
  });
}

function calculateMobileLayoutForAllWidgets(context, rootWidgets, parent) {
  const editorSession = context.editorSession;
  const widgetList = context.widgets;

  let layouts = {};

  // Helper function to calculate layout recursively for a widget and its children
  function calculateLayoutRecursively(widget) {
    if (_.includes([CONTENT, CONTENT_CARD, CONTENT_CARD_TEMPLATE, HOTSPOT], widget.type)) {
      return;
    }

    // Find all children of the current widget
    const children = widgetList.filter(child => child.parent === widget.id && !_.includes(IGNORE_WIDGETS_FOR_PROCESSING, child.type));

    if (!children || children.length === 0) {
      return;
    }

    const widgetRequests = generateWidgetRequestForDesktop(editorSession, [...children]);

    // Recursively calculate the layout for each child first
    widgetRequests.forEach(child => {
      calculateLayoutRecursively(child);
    });

    // After calculating child layouts, calculate the mobile layout for the current widget
    const mobileLayout = calculateMobileLayoutForWidgets(context, widgetRequests, children, widget);

    // Add the mobile layout for the current widget and its children to the final layouts
    Object.assign(layouts, mobileLayout);
  }
  
  // -------- Start calculating layout for each root widget  -------- //
  rootWidgets.forEach(rootWidget => {
    calculateLayoutRecursively(rootWidget);
  });

  const rootWidgetLayout = calculateMobileLayoutForWidgets(context, rootWidgets, widgetList, parent);

  Object.assign(layouts, rootWidgetLayout);

  // -------- Start calculating layout for FORM again  -------- //

  rootWidgets.forEach(rootWidget => {
    const rootParent = rootWidget.parent;    
    
    const parentId = parent.uid === rootParent ? parent.id ?? parent.uid : rootParent;

    context.optimizedWidgets[parentId]?.forEach((widget) => {
      if(widget.type === FORM) {
        context.formDetails[widget.id] = widget;
      }
    });

    calculateLayoutRecursively(rootWidget);
  });

  const formRootWidgetLayout = calculateMobileLayoutForWidgets(context, rootWidgets, widgetList, parent);
  
  Object.assign(layouts, formRootWidgetLayout);

  return layouts;
}

function calculateMobileLayoutForWidgets(context, widgetRequests, widgets, parent) {

  const editorSession = context.editorSession;

  const widgetsWithSections = calculateSectionsForWidgets(widgetRequests);
  let { remainingWidgets, overlaps } = removeOverlappingWidgets(widgetsWithSections);

  remainingWidgets = sortWidgets(remainingWidgets);

  let sections = createSectionsForWidgets(remainingWidgets, overlaps, context);
  sections = sections.map(section => calculateColumnsForSection(section, overlaps, context));

  let sectionsWithOverlaps = calculateOverlapsForSections(context, sections, overlaps);
  
  let sectionsWithGroupsOptimized = preprocessGroupWidgets(sectionsWithOverlaps, context);

  let sectionsWithMobileLayout = calculateMobileLayout(editorSession, context, sectionsWithGroupsOptimized);

  let mobileWidgets = calculateFinalPositions(sectionsWithMobileLayout, context);

  context.optimizedWidgets[parent.id] = mobileWidgets;

  let layouts = {};

  mobileWidgets.forEach(widget => {
    const layout = calculateLayoutFromWidget(editorSession, context, widget, widgets);
    layouts[widget.id] = layout;
  });

  return layouts;
}

function calculateLayoutFromWidget(editorSession, context, widget, widgets) {
  const widgetData = widgets.find(w => w.uid === widget.id);
  const parent = context.widgets.find(w => w.uid === _.get(widgetData, `parent`));
  const parentLayout = _.get(parent, `properties.desktop.layout`);
  const isFullWidthWidget = _.includes([SECTION_COLUMN, CAROUSEL_CARD], widgetData.type);
  let layout = _.get(widgetData, `properties.mobile.layout`);
  let desktopLayout = _.get(widgetData, `properties.desktop.layout`);
  const translateValues = desktopLayout?.transform?.translate?.split(",");

  var newLayout = new Frame(layout);

  if (widgetData?.properties?.mobile?.disableOptimize || widgetData?.properties?.mobile?.locked) {
    return newLayout.get();
  }

  var x = widget.x;
  var y = widget.y;

  if (isFullWidthWidget) {
    x = 0;
    y = 0;
  }

  if (parent?.type === GROUP && parseInt(parentLayout?.width) < 335) {
    x = parseInt(translateValues[0]);
    y = parseInt(translateValues[1]); 
  }

  const roundedValue = Math.round(widget.rotation);
  if (roundedValue != 0) {
    widget.width = widget.actualWidth;
    widget.height = widget.actualHeight;

    function rotateWidgetToFitLayout(x, y, w, h, angle) {
      // Step 1: Calculate the center of the widget
      const cx = x + w / 2;
      const cy = y + h / 2;

      // Step 2: Rotate the corners around the center
      const corners = [
        [x, y],                // Top-left
        [x + w, y],            // Top-right
        [x, y + h],            // Bottom-left
        [x + w, y + h]         // Bottom-right
      ];

      const rotatedCorners = corners.map(([xi, yi]) => {
        const xPrime = cx + (xi - cx) * Math.cos(angle) - (yi - cy) * Math.sin(angle);
        const yPrime = cy + (xi - cx) * Math.sin(angle) + (yi - cy) * Math.cos(angle);
        return [xPrime, yPrime];
      });

      // Step 3: Calculate the bounding box of the rotated shape
      const xMin = Math.min(...rotatedCorners.map(([xPrime]) => xPrime));
      const xMax = Math.max(...rotatedCorners.map(([xPrime]) => xPrime));
      const yMin = Math.min(...rotatedCorners.map(([, yPrime]) => yPrime));
      const yMax = Math.max(...rotatedCorners.map(([, yPrime]) => yPrime));

      const newWidth = xMax - xMin;
      const newHeight = yMax - yMin;

      // Step 4: Adjust the top-left corner to match the original position
      const newX = x - (xMin - x); // Translate so the top-left corner aligns
      const newY = y - (yMin - y);

      return {
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight
      };
    }

    const rotated = rotateWidgetToFitLayout(x, y, widget.width, widget.height, (roundedValue * Math.PI) / 180);
    x = rotated.x;
    y = rotated.y;
  }

  newLayout.set('width', `${widget.width}px`);
  newLayout.set('height', `${widget.height}px`);

  if (widget.type === GROUP) {
    const widgetWidth = parseInt(widget.width, 10) || 0;
    const adjustedWidth = Math.min(widgetWidth, 335);
    
    widget.minWidth = adjustedWidth;
    widget.minHeight = widget.height;
    newLayout.set("minHeight", `${parseInt(widget.height, 10) || 0}px`);
    newLayout.set("minWidth", `${adjustedWidth}px`);
    newLayout.set("width", `${adjustedWidth}px`);
  }

  if (parent?.type === GROUP && (parseInt(widget.width, 10) || 0) > 335) {
    newLayout.set("width", "335px");
  }

  if (isFullWidthWidget) {
    newLayout.set('width', `100%`);
  }

  if (newLayout.has('transform', 'translate')) {
    newLayout.set('transform', 'translate', `${x}px, ${y}px`);
  }

  newLayout.set('transform', 'rotate', `${widget.rotation}deg`);

  const widgetConfig = WidgetRegistry.moveableConfig(editorSession, widgetData.type, 'mobile');
  if (widgetConfig && widgetConfig.onResize) {
    newLayout = widgetConfig.onResize(widgetData, newLayout.get(), 'mobile');
    return newLayout;
  }

  return newLayout.get();
}

function preprocessGroupWidgets(sections, context) {
  sections.forEach(section => {
    section.columns.forEach(column => {
      column.widgets.forEach(widget => {
        const widgetData = context.widgets.find(w => w.uid === widget.id);
        const parent = context.widgets.find(w => w.uid === _.get(widgetData, 'parent'));
        
        if (parent?.type === GROUP) {
          const groupWidth = parseInt(parent.properties?.desktop?.layout?.width, 10) || 335;
          
          const groupChildren = column.widgets.filter(w => {
            const wData = context.widgets.find(wd => wd.uid === w.id);
            const wParent = context.widgets.find(wd => wd.uid === _.get(wData, 'parent'));
            return wParent?.uid === parent.uid;
          }).sort((a, b) => a.y - b.y || a.x - b.x);
          
          let currentRowY = groupChildren[0]?.y || 0;
          let currentRowWidth = 0;
          let rowStartIndex = 0;
          
          groupChildren.forEach((child, index) => {
            if (currentRowWidth + child.width > groupWidth && currentRowWidth > 0) {
              const previousRowMaxHeight = Math.max(...groupChildren.slice(rowStartIndex, index).map(w => w.height));
              currentRowY += previousRowMaxHeight + 10;
              currentRowWidth = child.width;
              rowStartIndex = index;
              
              child.x = 0;
              child.y = currentRowY + 50;
            } else {
              child.x = currentRowWidth;
              child.y = currentRowY + 50;
              currentRowWidth += child.width + 5;
            }
          });
        }
      });
    });
  });
  
  return sections;
}

function calculateMobileLayout(editorSession, context, data) {
  const mobileWidth = context.availableWidth;
  let sections = structuredClone(data);
  let currentY = 0; // Start y position for sections
  const sectionSpacing = 0; // Define space between sections
  const columnSpacing = 20; // Define horizontal space between columns
  const standardVerticalSpacing = 20; // Define standard vertical spacing between widgets  

  const lockedWidgets = context.widgets.filter(item =>
    !_.includes(IGNORE_WIDGETS_FOR_PROCESSING, item.type) &&
    !_.includes([SLIDE, PAGE], item.type) &&
    (item.properties?.mobile?.disableOptimize || item.properties?.mobile?.locked)
  );

  const lockedPositionsMap = {};
  lockedWidgets.forEach(widget => {
    const element = document.querySelector(`[data-widget-id="${widget.uid}"]`);
    
    const mobileTransform = widget.properties?.mobile?.layout?.transform?.translate || '';
    let mobileX = 0, mobileY = 0;
    
    if (mobileTransform) {
      const [xStr, yStr] = mobileTransform.split(',');
      mobileX = parseInt(xStr) || 0;
      mobileY = parseInt(yStr) || 0;
    }
    
    let width = parseInt(widget.properties?.mobile?.layout?.width) || 0;
    let height = parseInt(widget.properties?.mobile?.layout?.height) || 0;
    
    if (element) {
      const rect = element.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }
    
    lockedPositionsMap[widget.uid] = {
      x: mobileX,
      y: mobileY,
      width: width,
      height: height,
      widget: widget,
    };
  });

  // Calculate dimensions for all widgets
  sections = calculateMobileDimensions(editorSession, sections, context);

  sections = calculateRelativePositionForWidgets(sections);

  (sections || []).forEach((section) => {

    section.x = 0;
    section.y = currentY;
    section.width = mobileWidth;

    let sectionHeight = 0; // Accumulated height for the section
    let previousColumn = null;
    let currentColumnWidth = 0; // Tracks the horizontal width of the current column
    let currentRowWidth = 0; // Tracks the horizontal width of the current row of columns
    let rowStartY = 0; // Tracks the starting Y for the current row

    section.columns.forEach((column) => {
      let columnHeight = 0;
      let previousWidget = null;
      const containerWidgetTypes = [SECTION, SECTION_COLUMN, MENU, CAROUSEL, CAROUSEL_CARD, MODAL, ASSET, SEEK, CONTENT, GROUP, FORM, FORM_STEP];

      const calculateLayoutForWidget = (widget, isOverlapOuter, index) => {
        const isLocked = lockedWidgets.some(w => w.uid === widget.id);

        if (isLocked && lockedPositionsMap[widget.id]) {
          const lockedPos = lockedPositionsMap[widget.id];
          widget.x = lockedPos.x;
          widget.y = lockedPos.y;
          widget.width = lockedPos.width || widget.width;
          widget.height = lockedPos.height || widget.height;
          widget.lockedOffsetY = (lockedPos.y - currentY - (previousColumn ? previousColumn.height : 0) - rowStartY) || 0;
          return widget;
        }

        const newContext = calculateContextForWidget(context, widget);

        const isOverLap = isOverlapOuter && !containerWidgetTypes.includes(widget.type);

        const topOffset = newContext && !isOverLap ? newContext.topOffset : 0;
        const leftOffset = newContext && !isOverLap ? newContext.leftOffset : 0;
        const isPreviousWidgetLocked = previousWidget && lockedWidgets.some(w => w.uid === previousWidget.id);

        // Adjust the widget y position by placing it below the previous widget
        if (previousWidget) {
          let previousDesktopWidget = _.get(previousWidget, 'desktopLayout', {});
          if (previousWidget.type === 'overlap') {
            previousDesktopWidget = _.get(previousWidget.widgets[0], 'desktopLayout', {});
          }
          const currentDesktopWidget = _.get(widget, 'desktopLayout', {});
          const verticalSpacing = Math.max(0, currentDesktopWidget.y - (previousDesktopWidget.y + previousDesktopWidget.height));
          const spacing = verticalSpacing > 20 ? standardVerticalSpacing * 2 : standardVerticalSpacing;

          if(isPreviousWidgetLocked) {
            let maxY = 0;
            let allWidgetLocked = true;

            for (let i = 0; i < (index || 0); i++) {
              const baseRowOffsetY = rowStartY + (previousColumn ? previousColumn.height : 0);
              const absoluteCurrentY = currentY + baseRowOffsetY;
              const currentWidget = column.widgets[i];

              if(column.widgets[i] && !lockedPositionsMap[column.widgets[i].id]) {
                allWidgetLocked = false;
              }

              if((column.widgets.length - 1) === index && allWidgetLocked) {
                allWidgetLocked = false;
              }

              if (!currentWidget) continue; // Skip if widget missing or undefined

              const verticalOffsetFromBaseline = (allWidgetLocked || lockedPositionsMap[column.widgets[i].id]) ? ((currentWidget.y - absoluteCurrentY) + currentWidget.height) : (currentWidget.y + currentWidget.height);
              maxY = Math.max(verticalOffsetFromBaseline, maxY);
            }

            widget.y = (maxY || 0) + spacing;
          } else {
            let newY = previousWidget.y + previousWidget.height + spacing;
            widget.y = (newY || 0);
          }
        } else {
        // If it's the first widget in the section/column
          widget.y = ((currentY || previousColumn) && topOffset === 0) ? standardVerticalSpacing + topOffset : topOffset;
        }

        widget.x = leftOffset;

        const isNextToPrevious = previousWidget && (previousWidget.y + previousWidget.height < widget.y)
          && ![SECTION, SECTION_COLUMN, MENU, CAROUSEL, CAROUSEL_CARD, MODAL, SLIDE, ASSET, SEEK, CONTENT, FORM, FORM_STEP].includes(previousWidget.type);

        const canFitInline = previousWidget && previousWidget.width + previousWidget.x + (widget.width + columnSpacing) < (mobileWidth - leftOffset * 2);

        const isPreviousWide = previousWidget && previousWidget.width > 250;
        if (canFitInline && isNextToPrevious && !isPreviousWidgetLocked && !isPreviousWide) {

          const iscombineWide = (previousWidget && previousWidget?.width + widget?.width) > 335;
          const topPosition = previousWidget.y + previousWidget.height < widget.y;

          if (!iscombineWide && topPosition) {
            widget.x = previousWidget.x + previousWidget.width + (leftOffset || columnSpacing);
            widget.y = previousWidget.y;
          }
        }

        if(previousWidget) {
          currentColumnWidth += widget.width;
        }

        return widget;
      };

      const resizeParentByChildren = (parent, children, isOverLap) => {
        if (!children?.length) return parent; 
        const desktopLayoutWidth = parseInt(_.get(parent, "desktopLayout.width", 10)) || 0;
        const parentWidget = document.querySelector(`[data-widget-id="${parent.id}"]`);

        if(!parentWidget) return parent;
        const style = window.getComputedStyle(parentWidget);
        const paddingHeight = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

        if ([CONTENT, GROUP].includes(parent.type) && (parent.type === CONTENT || desktopLayoutWidth < 335)) {
          return parent;
        }

        const formParentTypes = [FORM, FORM_STEP];
        const fullWithParent = [SECTION, SECTION_COLUMN, MENU, CAROUSEL, CAROUSEL_CARD, MODAL, SLIDE, ASSET, SEEK, CONTENT].includes(parent.type) || (_.get(parent, 'desktopLayout.width', 0) >= mobileWidth && !formParentTypes.includes(parent.type));

        const containerWidgetTypes = [SECTION, FORM, CAROUSEL, MENU, MODAL, ASSET, SEEK, CONTENT, GROUP];

        const multiple = formParentTypes.includes(parent.type) ? 1 : 2;

        var left = Number.MAX_SAFE_INTEGER;
        var top = Number.MAX_SAFE_INTEGER;
        var bottom = 0;
        var right = 0;
      
        let maxLockedBottom = 0;
        let maxBottom = 0;
      
        const lockedChildrenIds = new Set();
        const lockedChildrenPositions = [];
      
        Object.values(lockedPositionsMap).forEach(lockedPos => {
          const lockedWidget = lockedPos.widget;
          if (lockedWidget.parent === parent.id) {
            lockedChildrenIds.add(lockedWidget.uid);
            lockedChildrenPositions.push(lockedPos);
          }
        });
      
        lockedChildrenPositions.forEach(lockedChild => {
          left = Math.min(lockedChild.x, left);
          top = Math.min(lockedChild.y, top);
          right = Math.max(right, lockedChild.x + lockedChild.width);
      
          const lockedBottom = lockedChild.y + lockedChild.height;

          maxLockedBottom = Math.max(maxLockedBottom, lockedBottom);
        });
      
        if (children?.length) {
          children.forEach(child => {
            if (!child.id || lockedChildrenIds.has(child.id)) return;
      
            left = Math.min(child.x, left);
            top = Math.min(child.y, top);
            right = Math.max(right, child.x + child.width);
            const childBottom = child.y + child.height;
            bottom = Math.max(bottom, childBottom);
      
            maxBottom = Math.max(maxBottom, childBottom);
          });
        }
      
        if (left === Number.MAX_SAFE_INTEGER || top === Number.MAX_SAFE_INTEGER) {
          return parent;
        }

        const width = Math.ceil(right - left) + columnSpacing * multiple;
      
        const unlockedHeight = maxBottom + (![SECTION, CAROUSEL, MENU, FORM].includes(parent.type) ? columnSpacing * 2 : 0);
        const lockedHeight = maxLockedBottom + columnSpacing;
      
        const height = Math.max(unlockedHeight, lockedHeight) + (paddingHeight || 0);
      
        parent.width = fullWithParent ? context.availableWidth : Math.min(context.availableWidth, width);
        parent.height = height;

        // OVERLAP CASE
        if(containerWidgetTypes.includes(parent.type) && isOverLap) {
          const childWidget = context.optimizedWidgets[parent.id];
          let parentHeight = 0;
          let parentWidth = 0;

          for(let i = 0; i < childWidget?.length; i++) {
            const child = childWidget[i];
            const bottom = (child.lockedOffsetY ?? child.y) + child.height;
            parentHeight = Math.max(bottom, parentHeight);
            parentWidth = Math.max(child.width, parentWidth);
          }

          const width = fullWithParent ? context.availableWidth : Math.min(context.availableWidth, parentWidth + (columnSpacing * multiple));
          const height = Math.max((parentHeight + paddingHeight || 0), parent.height);

          parent.width = width;
          parent.height = height;  
        }

        // ----- Customize Form Layout ------ //
        if(formParentTypes.includes(parent.type) && parent.width >= 335) {
          parent.width = 335;
        }

        return parent;
      }

      column.widgets.forEach((widget, index) => {
        const widgetData = context.widgets.find(w => w.uid === widget.id);
        const parent = context.widgets.find(w => w.uid === _.get(widgetData, 'parent'));

        if (_.get(parent, 'type') === GROUP) {
          const desktopLayout = _.get(widgetData, 'properties.desktop.layout', {});
          const parentLayout = _.get(parent, "properties.desktop.layout", {});
          const frame = new Frame(desktopLayout)
         
          const translate = frame.get("transform", "translate") || "0,0";
          const [x, y] = _.split(translate, ",").map((val) => parseInt(val, 10) || 0);
          
          if (parseInt(parentLayout?.width, 10) < 335) {
            widget.x = x;
            widget.y = y;
        
            const layoutWidth = parseInt(desktopLayout.width, 10);
            const layoutHeight = parseInt(desktopLayout.height, 10);
        
            widget.width = !isNaN(layoutWidth) ? layoutWidth : parseInt(widget.width, 10) + 1;
            widget.height = !isNaN(layoutHeight) ? layoutHeight : parseInt(widget.height, 10);
        
            return;
          }
        }

        if (widget.type === 'overlap') {
          let overlappingWidgets = widget.widgets;
          let outerWidget = overlappingWidgets.find(w => w.overlap);
          let innerWidgets = overlappingWidgets.filter(w => !w.overlap);
          innerWidgets = calculateMobileLayoutForInnerWidgets(context, outerWidget, innerWidgets);

          const containerOuterWidget = containerWidgetTypes.includes(outerWidget.type);

          const hasLockedInnerWidgets = innerWidgets.some(innerWidget => 
            lockedWidgets.some(lockedWidget => lockedWidget.uid === innerWidget.id)
          );

          if (hasLockedInnerWidgets && !containerOuterWidget) {
            const outerElement = document.querySelector(`[data-widget-id="${outerWidget.id}"]`);
            
            if (outerElement) {
              const rect = outerElement.getBoundingClientRect();
              
              outerWidget.width = rect.width;
              outerWidget.height = rect.height;
              
              outerWidget = calculateLayoutForWidget(outerWidget, true, index);
            }
          } else {
            outerWidget = calculateLayoutForWidget(outerWidget, true, index);

            if (_.get(outerWidget, 'desktopLayout.width', 0) > mobileWidth) {
              outerWidget = resizeParentByChildren(outerWidget, innerWidgets, true);
            } 
  
            overlappingWidgets = [outerWidget, ...innerWidgets];
            widget.widgets = overlappingWidgets;
  
            widget = createOverlapForWidgets(overlappingWidgets);
          }

          widget.height = outerWidget.height;
          widget.y = (outerWidget.lockedOffsetY ?? outerWidget.y);

        } else {
          widget = calculateLayoutForWidget(widget, false, index);
        }

        resolveOverlap({
          context, 
          widget, 
          lockedPositionsMap,
          rowStartY, 
          previousColumn,
          currentY
        });

        if(widget.type !== 'overlap') {
          const children = context.optimizedWidgets[widget.id];
          widget = resizeParentByChildren(widget, children); 
        }

        // ----- Customize Form Layout ------ //
        const formId = parentRoot(context.widgets, widget.id, widget.parent, [FORM]);

        if(context.formDetails?.[formId] && ![FORM, FORM_STEP].includes(widget.type)) {
          const formDetails = context.formDetails[formId];
          const widgetParent = widget.parent;

          if(context.optimizedWidgets[widgetParent] && formDetails) {
            const optimizedWidget = context.optimizedWidgets[widgetParent].find((w) => w.id === widget.id);

            if(optimizedWidget) {
              const currentRight = optimizedWidget.x + optimizedWidget.width;

              const maxRight = Math.min(formDetails.width ?? 335, 335);

              if (currentRight >= maxRight) {
                const FORM_WIDGET_MARGIN = 40;
                const newWidth = Math.max(0, maxRight - FORM_WIDGET_MARGIN);

                // store old values
                const oldWidth = widget.width;
                const oldHeight = widget.height;

                widget.width = newWidth;

                // ----- Proportional Image ------ //
                if (widget.type === IMAGE && oldWidth > 0) {
                  const scale = newWidth / oldWidth;
                  widget.height = oldHeight * scale;
                }
              }
            }
          }
        }

        if([SECTION, LIVE_ELEMENT, CONTENT, MENU, SEEK].includes(widget.type)) {
          widget.x = 0;
        }

        previousWidget = widget;
      });

      let maxX = 0;
      let maxRight = 0;

      for(let i = 0; i < column.widgets.length; i++) {
        const right = column.widgets[i].x + column.widgets[i].width;
        maxRight = Math.max(right, maxRight);
        maxX = Math.max(column.widgets[i].width, maxX);
      }

      // Check if the column fits in the current row
      const fitsInRow =
      maxRight + (previousColumn ? columnSpacing + currentRowWidth : 0) <= mobileWidth;

      if (fitsInRow) {
        column.x = (previousColumn ? columnSpacing + currentRowWidth : 0);
        column.y = rowStartY;
        currentRowWidth += maxRight + (previousColumn ? columnSpacing : 0);
      } else {
        // Move to the next row if columns don't fit
        rowStartY += previousColumn ? previousColumn.height + columnSpacing : 0;
        column.x = 0;
        column.y = rowStartY;
        currentRowWidth = maxRight;
      }

      for(let i = 0; i < column.widgets.length; i++) {
        const widget = column.widgets[i];
        let y = widget.lockedOffsetY ?? widget.y;

        if(widget.type === 'overlap') {
          const outerWidget = widget.widgets.find(w => w.overlap);
          widget.y = outerWidget.lockedOffsetY ?? outerWidget.y;
          widget.height = outerWidget.height;
          y = widget.y;
        }

        const bottom = y + widget.height;
        columnHeight = Math.max(bottom, columnHeight);
      }

      column.height = columnHeight;

      previousColumn = column;
    });

    // Set the section height based on the bottom of the last row of columns
    sectionHeight = rowStartY + (previousColumn ? previousColumn.height : 0);
    section.height = sectionHeight;

    currentY += sectionHeight + sectionSpacing; // Update the Y position for the next section
  });

  const finalAdjustSectionHeights = (sections) => {
    sections.forEach(section => {
      // Find the maximum Y-coordinate of any widget (locked or not) in this section
      let maxY = 0;
      
      // Check all non-locked widgets
      section.columns.forEach(column => {
        column.widgets.forEach(widget => {
          const bottomY = widget.y + widget.height;
          maxY = Math.max(maxY, bottomY);
        });
      });
      
      // Also check locked widgets that belong to this section
      lockedWidgets.forEach(lockedWidget => {
        // Check if this locked widget belongs to the current section
        const widgetSection = getWidgetSection(lockedWidget, sections);
        if (widgetSection && widgetSection.id === section.id) {
          const lockedPos = lockedPositionsMap[lockedWidget.uid];
          if (lockedPos) {
            const bottomY = lockedPos.y + lockedPos.height;
            maxY = Math.max(maxY, bottomY);
          }
        }
      });
      // Ensure section height accommodates all widgets, including locked ones
      section.height = Math.max(section.height, maxY - section.y + 20); // Add some padding

    });

    return sections;
  };
  
  // Helper function to determine which section a widget belongs to
  const getWidgetSection = (widget, sections) => {
    
    for (const section of sections) {
      // Example implementation - determine section by y-coordinate
      const widgetY = widget.properties?.mobile?.layout?.transform?.translate?.split(',')[1] || 0;
      const parsedY = parseInt(widgetY) || 0;
      
      if (parsedY >= section.y && parsedY < section.y + section.height) {
        return section;
      }
    }
    
    return null; // Widget doesn't belong to any section
  };

  sections = calculateRelativePositionForWidgets(sections);
  sections = finalAdjustSectionHeights(sections);

  return sections;
}

function resolveOverlap({
  context, 
  widget, 
  lockedPositionsMap,
  rowStartY, 
  previousColumn,
  currentY
}) {

  const containerWidgetTypes = [SECTION, SECTION_COLUMN, MENU, CAROUSEL, CAROUSEL_CARD, MODAL, ASSET, SEEK, CONTENT, GROUP, FORM, FORM_STEP, MENU_DRAGGABLE_CONTAINER, MENU_CONTAINER];
  const subContainerWidgetTypes = [FORM_STEP, SECTION_COLUMN, CAROUSEL_CARD];

  if(subContainerWidgetTypes.includes(widget.type)) return;

  const newContext = calculateContextForWidget(context, widget);
  const leftOffset = newContext ? newContext.leftOffset : 0;

  const standardVerticalSpacing = 20;

  // helper to get absolute Y for comparison (matches your original idea)
  function computeWidgetY(w) {
    return w.y + currentY + (rowStartY || 0) + (previousColumn ? (previousColumn.height) : 0);
  }

  function overlaps(a, b) {
    const ax1 = a.x, ay1 = computeWidgetY(a), ax2 = ax1 + a.width, ay2 = ay1 + a.height;
    const bx1 = b.x, by1 = b.y, bx2 = bx1 + b.width, by2 = by1 + b.height;
    const horiz = ax1 <= bx2 && ax2 >= bx1;
    const vert = ay1 <= by2 && ay2 >= by1;
    return horiz && vert;
  }

  let hasOverlap = true;
  const maxIterations = 10;
  let iterations = 0;

  while (hasOverlap && iterations < maxIterations) {
    hasOverlap = false;
    iterations++;

    for (const locked of Object.values(lockedPositionsMap)) {
      if (lockedPositionsMap[widget.id]) continue; // skip self
  
      if(widget.type === 'overlap') {
        const overlappingWidgetIds = widget.widgets.map(w => w.id);
        if (overlappingWidgetIds.includes(locked.widget.uid)) {
          continue; // skip if the locked widget is part of the overlap
        }
      }

      const commonAncestor = findParentRoot(context.widgets, widget.id, locked.widget.uid, containerWidgetTypes);

      const parent = findAllParents(context.widgets, locked.widget.uid, containerWidgetTypes);
      if(parent && containerWidgetTypes.includes(widget.type)) continue; // skip if child is in container and parent is locked

      if (overlaps(widget, locked) && commonAncestor) {
        // try move right
        const rightX = locked.x + locked.width + standardVerticalSpacing;
        const fitsRight = (rightX + widget.width) <= context.availableWidth;

        if (fitsRight) {
          widget.x = rightX;
        } else {
          // move below and align to leftOffset
          const desiredY = locked.y + locked.height + standardVerticalSpacing;
          const currentAbsY = computeWidgetY(widget);
          const adjustment = desiredY - currentAbsY;          
          widget.x = rightX + widget.width > context.availableWidth ? leftOffset : rightX;

          widget.y = (widget.y || 0) + adjustment;
          if (widget.y < 0) widget.y = 0;
        }

        hasOverlap = true;
      }
    }
  }

  return widget;
}

function findAllParents(widgets, childId, containerTypes) {
  // find the child node
  const child = widgets.find(w => w.uid === childId);

  if (!child || !child.parent) return null;

  // find the parent node
  const parent = widgets.find(w => w.uid === child.parent);
  if (!parent) return null;

  // only include if NOT in containerWidgetTypes
  if (containerTypes.includes(parent.type)) {
    return parent.uid;
  }

  // recurse upward, even if parent is a container (so we can still check its parent)
  return findAllParents(widgets, parent.uid, containerTypes);
}

function parentRoot(widgets, childId, parentId, containerTypes) {
  // find the child node
  const child = widgets.find(w => w.uid === childId);

  if (!child || !child.parent) return parentId;

  parentId = child.parent;

  // find the parent node
  const parent = widgets.find(w => w.uid === child.parent);

  if (!parent) return parentId;

  // only include if NOT in containerWidgetTypes
  if (containerTypes.includes(parent.type)) {
    return parent.uid;
  }  

  // recurse upward, even if parent is a container (so we can still check its parent)
  return parentRoot(widgets, parent.uid, parentId, containerTypes);
}

function findParentRoot(widgets, childIdA, childIdB, containerWidgetTypes) {
  let parentId = null;

  const parentsA = parentRoot(widgets, childIdA, parentId, containerWidgetTypes);
  const parentsB = parentRoot(widgets, childIdB, parentId, containerWidgetTypes);
  
  // If either has no parents, return false
  if (parentsA === parentsB) {
    return parentsA;
  }

  return null;
}

function calculateMobileLayoutForInnerWidgets(context, outerWidget, widgets) {  
  const mobileWidth = context.availableWidth;
  let previousWidget = null;
  let currentRowWidth = 0;

  const calculateLayoutForWidget = (widget) => {
    const newContext = calculateContextForWidget(context, widget);

    const topOffset = newContext ? newContext.topOffset : 0;
    const leftOffset = newContext ? newContext.leftOffset : 0;

    const isNextToPrevious =
      previousWidget &&
      (widget.y === previousWidget.y || widget.y < previousWidget.y + previousWidget.height);

    const canFitInline = currentRowWidth + widget.width <= mobileWidth;

    if (canFitInline && isNextToPrevious) {
      widget.x = previousWidget.x + previousWidget.width + leftOffset;
    } else {
      widget.x = leftOffset;

      // Adjust the widget y position by placing it below the previous widget
      if (previousWidget) {
        let heightDiff = 0;
        if (previousWidget.type === TEXT && _.get(previousWidget, 'desktopLayout.height', 0) >= previousWidget.height) {
          heightDiff = _.get(previousWidget, 'desktopLayout.height', 0) - previousWidget.height;
        }

        if (widget.type === TEXT && _.get(widget, 'desktopLayout.height', 0) > widget.height) {
          heightDiff = _.get(widget, 'desktopLayout.height', 0) - widget.height;
        }

        let newY = previousWidget.y + previousWidget.height + heightDiff + topOffset;
        widget.y = newY;
      } else {
        widget.y = topOffset; // If it's the first widget in the section/column
      }
    }

    previousWidget = widget;

    currentRowWidth += widget.width;

    return widget;
  };

  if (_.get(outerWidget, 'desktopLayout.width', 0) < mobileWidth) {
    widgets = widgets.map(widget => {
      const xDiff = _.get(widget, 'desktopLayout.x', 0) - _.get(outerWidget, 'desktopLayout.x', 0);
      const yDiff = _.get(widget, 'desktopLayout.y', 0) - _.get(outerWidget, 'desktopLayout.y', 0);
      
      return {
        ...widget,
        x: xDiff,
        y: yDiff
      }
    });

    return widgets;
  }

  widgets = widgets.map(innerWidget => {
    return calculateLayoutForWidget(innerWidget);
  })

  return widgets;
}

function calculateOverlapsForSections(context, data, overlaps) {
  let sections = structuredClone(data);

  sections.forEach(section => {
    section.columns.forEach(column => {
      
      let updatedWidgets = [];

      column.widgets.forEach(widget => {
        const overlappingIds = overlaps[widget.id];
        if (!overlappingIds) {
          updatedWidgets.push(widget);
          return;
        }

        const innerWidgets = context.widgets.filter(w => overlappingIds.includes(w.uid));
        let overlappingWidgets = calculateLayoutForOverlappedWidgets(context, innerWidgets, widget);

        if(overlappingWidgets.length > 0) {
          overlappingWidgets = [{...widget, overlap: true}, ...overlappingWidgets];
          const overlap = createOverlapForWidgets(overlappingWidgets);

          updatedWidgets.push(overlap);
        } else {
          updatedWidgets.push(widget);
        }
      });

      column.widgets = updatedWidgets;
    });
  })

  return sections;
}

function createOverlapForWidgets(widgets) {
  var left = Number.MAX_SAFE_INTEGER;
  var top = Number.MAX_SAFE_INTEGER;
  var bottom = 0;
  var right = 0;

  widgets.forEach(widget => {
    left = Math.min(widget.x, left);
    top = Math.min(widget.y, top);

    right = Math.max(right, widget.x + widget.width);
    bottom = Math.max(bottom, widget.y + widget.height);
  })

  widgets = sortWidgets(widgets);

  const width = Math.ceil(right - left);
  const height = Math.ceil(bottom - top);

  return {
    id: `overlap_${shortid.generate()}`,
    type: 'overlap',
    x: left,
    y: top,
    width: width,
    height: height,
    widgets: widgets
  }
}

function calculateLayoutForOverlappedWidgets(context, innerWidgets, outerMobileWidget) {
  let overlappingWidgets = [];

  const outerWidget = context.widgets.find(w => w.uid === outerMobileWidget.id);
  // const innerWidgets = context.widgets.filter(w => innerIds.includes(w.uid));

  const outerDesktopRect = getWidgetBoundingRectForBreakpoint(context.editorSession, outerWidget, 'desktop');

  innerWidgets.forEach(innerWidget => {
    const innerDesktopRect = getWidgetBoundingRectForBreakpoint(context.editorSession, innerWidget, 'desktop');
    const innerRect = getWidgetBoundingRectForBreakpoint(context.editorSession, innerWidget, 'desktop');

    const xDiff = innerDesktopRect.x - outerDesktopRect.x;
    const yDiff = innerDesktopRect.y - outerDesktopRect.y;

    const outerX = outerMobileWidget.x;
    const outerY = (outerMobileWidget.lockedOffsetY ?? outerMobileWidget.y);

    const innerWidth = innerRect.width;
    const innerHeight = innerRect.height;

    const newInnerX = outerX + xDiff;
    const newInnerY = outerY + yDiff;

    innerWidget.x = newInnerX;
    innerWidget.y = newInnerY;

    overlappingWidgets.push({
      id: innerWidget.uid,
      type: innerWidget.type,
      x: newInnerX,
      y: newInnerY,
      width: innerWidth,
      height: innerHeight
    });
  })

  return overlappingWidgets;
}


function calculateMobileDimensions(editorSession, sections, context) {

  const calculateMobileDimensionsForWidget = (editorSession, widget, isOverlapOuter) => {
    const wDesktopElement = getWidgetElementForBreakpoint(editorSession, widget.id, 'desktop');
    const wElement = getWidgetElementForBreakpoint(editorSession, widget.id, 'mobile');

    if (!wElement || !wDesktopElement) {
      return widget; 
    }

    const wDesktopRect = wDesktopElement.getBoundingClientRect();
    const wRect = wElement.getBoundingClientRect();

    widget.desktopLayout = wDesktopRect;

    const newContext = calculateContextForWidget(context, widget);
    let leftOffset = isOverlapOuter ? 0 : newContext.leftOffset;

    const availableWidth = context.availableWidth - (leftOffset * 2);

    let finalWidth = wDesktopRect.width < availableWidth ? wDesktopRect.width : availableWidth;
    let finalHeight = wDesktopRect.width < availableWidth ? wDesktopRect.height : wRect.height;

    // let aspectRatio = wDesktopRect.height / wDesktopRect.width;
    // if (aspectRatio !== 0) {
    //   finalHeight = finalWidth * aspectRatio;
    // }

    if (_.includes([TEXT, CONTENT, CONTENT_CARD, MENU, MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER, CAROUSEL, CAROUSEL_CARD, ASSET, ...formTypes], widget.type)) {
      finalWidth = wRect.width;
      finalHeight = wRect.height;
    }

    const transform = wDesktopElement.style.transform;

    // Match "rotate(...deg)" within the transform string
    const rotationMatch = transform?.match(/rotate\(([^)]+)deg\)/);
    const rotation = rotationMatch ? parseFloat(rotationMatch[1]) : 0;

    widget.rotation = ((rotation % 360) + 360) % 360; 

    if (rotation === 0) {
      widget.width = finalWidth;
      widget.height = finalHeight;
      return widget;
    }

    const widgetRectWithoutRotation = calculateDimensionWithRotation(widget, 0, widget.width, widget.height, 'desktop');

    let actualWidth = widgetRectWithoutRotation.width < availableWidth ? widgetRectWithoutRotation.width : availableWidth;
    let actualHeight = widgetRectWithoutRotation.width < availableWidth ? widgetRectWithoutRotation.height : wRect.height;

    let actualRotio = widgetRectWithoutRotation.height / widgetRectWithoutRotation.width;
    if (actualRotio !== 0) {
      actualHeight = actualWidth * actualRotio;
    }

    widget.actualWidth = actualWidth;
    widget.actualHeight = actualHeight; 

    const widgetRectWithRotation = calculateDimensionWithRotation(widget, rotation, actualWidth, actualHeight, 'desktop');
    widget.width = widgetRectWithRotation.width;
    widget.height = widgetRectWithRotation.height;

    return widget;
  }

  function calculateDimensionWithRotation(widget, rotation, width, height, breakpoint) {
    var tempElement = document.createElement('div');
    tempElement.style.visibility = 'hidden';

    const wElement = getWidgetElementForBreakpoint(editorSession, widget.id, breakpoint);
    let tempWidgetElement = wElement.cloneNode(true);
    tempElement.appendChild(tempWidgetElement);

    document.body.appendChild(tempElement);

    const transform = wElement.style.transform;

    // Match `translate`, `rotate`, and `scale` values from the transform string
    const translateMatch = transform.match(/translate\(([^)]+)\)/);
    const scaleMatch = transform.match(/scale\(([^)]+)\)/);
    const rotateMatch = transform.match(/rotate\(([^)]+)\)/);

    // Extract values or default to identity
    const translate = translateMatch ? translateMatch[1] : "0px, 0px";
    const scale = scaleMatch ? scaleMatch[1] : "1, 1";

    // Reconstruct the transform with rotation set to 0
    const newTransform = `translate(${translate}) rotate(${rotation}deg) scale(${scale})`;

    // Apply the updated transform to the element
    tempWidgetElement.style.transform = newTransform;
    if (rotation != 0) {
      tempWidgetElement.style.width = `${width}px`;
      tempWidgetElement.style.height = `${height}px`;
    }

    var tempRect = tempWidgetElement.getBoundingClientRect();
    document.body.removeChild(tempElement);

    return tempRect;
  }

  const recalculateColumnDimentions = (column) => {
    const children = column.widgets || [];

    var left = Number.MAX_SAFE_INTEGER;
    var top = Number.MAX_SAFE_INTEGER;
    var bottom = 0;
    var right = 0;

    children.forEach(child => {
      left = Math.min(child.x, left);
      top = Math.min(child.y, top);

      right = Math.max(right, child.x + child.width);
      bottom = Math.max(bottom, child.y + child.height);
    })

    const width = Math.ceil(right - left);
    const height = Math.ceil(bottom - top);

    column.width = width;
    column.height = height;

    return column;
  }

  const keepDesktopDimensionsToMobile = (widget) => {
    const wDesktopElement = getWidgetElementForBreakpoint(editorSession, widget.id, 'desktop');
    const wElement = getWidgetElementForBreakpoint(editorSession, widget.id, 'mobile');

    if (!wElement || !wDesktopElement) {
      return widget; 
    }

    const wDesktopRect = wDesktopElement.getBoundingClientRect();
    const wRect = wElement.getBoundingClientRect();

    widget.width = wDesktopRect.width;
    widget.height = wDesktopRect.height;
    widget.desktopLayout = wDesktopRect;

    return widget;
  }

  sections.forEach(section => {
    section.columns.forEach(column => {
      column.widgets.forEach(widget => {
        const widgetData = context.widgets.find(w => w.uid === widget.id);
        const parent = context.widgets.find(w => w.uid === _.get(widgetData, 'parent', null));
        const desktopLayout = _.get(widgetData, 'properties.desktop.layout', {});

        if(widget.type === 'overlap') {
          widget.widgets.forEach((innerWidget, index) => {
            if (index === 0) {
              innerWidget = calculateMobileDimensionsForWidget(editorSession, innerWidget, true);
            } else {
              innerWidget = calculateMobileDimensionsForWidget(editorSession, innerWidget, false);
            }
          })
        } if ((widget.type === GROUP || _.get(parent, 'type', null) === GROUP) && (parseInt(desktopLayout?.width) < 335)) {
            widget = keepDesktopDimensionsToMobile(widget);
        } else {
          widget = calculateMobileDimensionsForWidget(editorSession, widget, false);
        }
      });

      column = recalculateColumnDimentions(column);
    });
  });

  return sections;
}

function calculateRelativePositionForWidgets(sections) {
  if (!sections || sections.length === 0) {
    return sections;
  }

  let lastSectionBottom = 0;  // This will store the bottom Y-coordinate of the last section
  let firstSectionX = sections[0].x; // Store the x-coordinate of the first section

  sections.forEach((section, index) => {
    // For the first section, set relativeY to 0 and relativeX to 0
    if (index === 0) {
      section.relativeY = 0;
      section.relativeX = 0;
    } else {
      // For subsequent sections, set relativeY based on the last section's bottom position
      section.relativeY = lastSectionBottom;
      // Set relativeX based on the difference from the first section's x
      section.relativeX = section.x - firstSectionX;
    }

    // Update lastSectionBottom to the bottom of the current section
    lastSectionBottom = section.relativeY + section.height;

    // Process each column in the section
    section.columns.forEach(column => {
      // Calculate column's relative coordinates with respect to the section's origin
      column.relativeX = column.x - section.x;
      column.relativeY = column.y - section.y;

      // Process each widget in the column
      column.widgets.forEach(widget => {
        // Calculate widget's relative coordinates with respect to the column's origin
        widget.relativeX = widget.x - column.x;
        widget.relativeY = widget.y - column.y;

        if(widget.type === 'overlap') {
          widget.widgets.forEach(innerWidget => {
            innerWidget.relativeX = innerWidget.x - widget.x;
            innerWidget.relativeY = (innerWidget.lockedOffsetY ?? innerWidget.y) - widget.y;
          })
        }
      });
    });
  });

  return sections;
}

function calculateFinalPositions(sections, context) {
  let currentY = 0; // Start y position for the first section

  const sectionSpacing = 20;
  const flattenedWidgets = [];

  sections.forEach(section => {
    let sectionX = section.x; // Starting x position of the section
    let sectionY = section.y; // Y position of the current section

    // Iterate through each column in the section
    section.columns.forEach((column) => {
      let columnY = sectionY + column.y; // The starting Y position for this column
      let columnX = sectionX + column.x; // Adjust x position for columns after the first one

      column.widgets.forEach((widget, index) => {
        if(widget.type === 'overlap') {
          const overlappingWidgets = widget.widgets;
          const outerWidget = overlappingWidgets.find(w => w.overlap);  
          const innerWidgets = overlappingWidgets.filter(w => !w.overlap);

          outerWidget.x = columnX + outerWidget.x;
          outerWidget.y = columnY + (outerWidget.lockedOffsetY ?? outerWidget.y);

          flattenedWidgets.push(outerWidget);

          innerWidgets.forEach(innerWidget => {
            innerWidget.x = outerWidget.x + innerWidget.x;
            innerWidget.y = outerWidget.y + innerWidget.y;

            flattenedWidgets.push(innerWidget);
          })

          return;
        }
        // Calculate the final widget position
        let finalX = columnX + widget.x;
        let finalY = columnY + widget.y;

        if (index === 0) {
          column.x = finalX;
          column.y = finalY;
        }

        // Add the widget with its final position to the flattened list
        flattenedWidgets.push({
          ...widget,
          id: widget.id,
          type: widget.type,
          x: finalX,
          y: finalY,
          width: widget.width,
          height: widget.height,
          rotation: widget.rotation
        });

      });
    });
  });
  return flattenedWidgets;
}

function calculateColumnsForSection(section, overlaps, context) {
  let subSections = [];
  let verticalGaps = [];
  let sectionWidgets = section.widgets;

  let gaps = calculateHorizontalGaps(sectionWidgets);

  if (!gaps || gaps.length === 0) {
    subSections.push(sectionWidgets);
  } else {
    gaps.sort((a, b) => a.y - b.y);
    subSections.push(sectionWidgets.filter(elem => elem.y <= gaps[0].y));

    for (let i = 0; i < gaps.length - 1; i++) {
      const upperGap = gaps[i];
      const lowerGap = gaps[i + 1];

      const lowerY = upperGap.y + upperGap.height;
      const upperY = lowerGap.y;

      subSections.push(sectionWidgets.filter(w => lowerY <= w.y && w.y <= upperY));
    }

    subSections.push(sectionWidgets.filter(w => w.y >= gaps[gaps.length - 1].y));
  }

  // Within each sub section, get all vertical gaps
  subSections.forEach(subSection => {
    verticalGaps = verticalGaps.concat(calculateVerticalGaps(subSection));
  })

  const combinedVerticalGaps = combineVerticalGaps(verticalGaps);

  const widgetsWithColumn = getColumns(sectionWidgets, combinedVerticalGaps);
  const sortedWidgetsWithColumn = sortColumnElements(widgetsWithColumn);

  let finalWidgetsWithColumn = sortedWidgetsWithColumn.flat();
  finalWidgetsWithColumn = sortWidgets(finalWidgetsWithColumn);

  section.widgets = finalWidgetsWithColumn;

  section = createColumnsForSection(section, overlaps, context);

  return section;
}

function calculateHorizontalGaps(elements) {
  /**
   * Gets all horizontal gaps in the webpage layout.
   * Adds the horizontal gap as an element with "id": "horizontal gap".
   */

  // Fetching top-left and bottom-left y-values of all elements
  const verticalSpans = elements.map(el => [el.y, el.y + el.height]);

  // Sorting based on y coordinate of elements
  verticalSpans.sort((a, b) => a[0] - b[0]);

  let horizontalSections = [];
  let maxY = Math.min(...elements.map(el => el.y)); // Starting from the topmost y-value

  // Iterating through all vertical spans
  for (let [start, end] of verticalSpans) {
    // If there's a horizontal gap, record it
    if (start > maxY) {
      horizontalSections.push([maxY, start]);
    }
    maxY = Math.max(maxY, end);
  }

  // Creating gap elements for the horizontal gaps
  const gaps = horizontalSections.map((section, idx) => ({
    x: 0,
    y: section[0],
    width: 1200,
    height: section[1] - section[0],
    id: `horizontal_gap_${idx}`,
    type: "gap",
  }));

  return gaps;
}

function calculateVerticalGaps(elements) {
  /**
   * Get all vertical gaps between elements
   * Return these vertical gaps as elements along with the original elements, with appropriate width and height for visualization
   */

  // Pre-calculate min y, max y + height for performance improvement
  const minY = Math.min(...elements.map(el => el.y));
  const maxY = Math.max(...elements.map(el => el.y + el.height));

  // Calculate horizontal spans and sort them
  const horizontalSpans = elements
    .map(el => [el.x, el.x + el.width])
    .sort((a, b) => a[0] - b[0]);

  const verticalSections = [];
  let maxX = horizontalSpans[0][0]; // Start with the first x position

  // Calculate vertical gaps
  for (const [start, end] of horizontalSpans) {
    if (start > maxX) {
      verticalSections.push([maxX, start]);
    }
    maxX = Math.max(maxX, end);
  }

  // Create gap objects only once based on pre-calculated values
  return verticalSections.map(([startX, endX]) => ({
    x: startX,
    y: minY,
    width: endX - startX,
    height: maxY - minY,
    id: "v",
    type: "gap"
  }));
}

function combineVerticalGaps(verticalGaps) {
  if (!verticalGaps.length) return [];

  let combined = false;

  do {
    combined = false;
    const result = [];
    let i = 0;

    // Iterate through the vertical gaps and combine intersecting ones
    while (i < verticalGaps.length) {
      const lowerGap = verticalGaps[i];
      const upperGap = verticalGaps[i + 1];

      if (i + 1 < verticalGaps.length) {
        const bx1 = upperGap.x, bx2 = upperGap.x + upperGap.width;
        const ax1 = lowerGap.x, ax2 = lowerGap.x + lowerGap.width;

        // If two subsequent gaps intersect, combine them
        if (!(ax2 < bx1 || ax1 > bx2)) {
          combined = true;
          result.push({
            id: "v",
            type: "gap",
            y: Math.min(upperGap.y, lowerGap.y),
            x: Math.max(ax1, bx1),
            width: Math.min(ax2, bx2) - Math.max(ax1, bx1),
            height: (upperGap.y + upperGap.height) - lowerGap.y
          });
          i += 2; // Skip the next gap since it's combined
        } else {
          result.push(lowerGap);
          i++;
        }
      } else {
        result.push(lowerGap); // Push the last remaining gap if no more combinations are possible
        i++;
      }
    }

    verticalGaps = result;
  } while (combined);

  return verticalGaps;
}

function getColumns(elements, verticalGaps) {
  /**
   * Get all columns in each sub-section and assign column numbers to each element.
   */

  elements.sort((a, b) => a.y - b.y);
  verticalGaps.sort((a, b) => a.y - b.y);

  // If there are no vertical gaps, assign all elements to a single column
  if (!verticalGaps.length) {
    return elements.map(el => {
      return {
        ...el,
        column: 1
      }
    });
  }

  let seenGaps = new Set();
  let groupedColumns = [];
  let remainingElements = elements.slice();

  // Map gaps to element columns
  for (let verticalGap of verticalGaps) {
    if (seenGaps.has(verticalGap)) continue;

    const lowerGapY = verticalGap.y;
    const upperGapY = lowerGapY + verticalGap.height;

    // Get intersecting elements and break early if no elements found
    const intersectingElements = remainingElements.filter(el => el.y + el.height > lowerGapY && el.y < upperGapY);
    if (!intersectingElements.length) continue;

    const intersectingElementsLowerY = intersectingElements[0].y;
    const intersectingElementsUpperY = intersectingElements[intersectingElements.length - 1].y + intersectingElements[intersectingElements.length - 1].height;

    // Check which gaps intersect with the current set of intersecting elements
    const intersectingGaps = verticalGaps.filter(gap =>
      gap.y + gap.height > intersectingElementsLowerY && gap.y < intersectingElementsUpperY
    );

    // Assign column number based on the horizontal position relative to intersecting gaps
    groupedColumns.push(intersectingElements.map(el => {
      let column = intersectingGaps.length + 1; // Default column if no gaps are to the right
      for (let index = 0; index < intersectingGaps.length; index++) {
        const gap = intersectingGaps[index];
        if (gap.x > el.x) {
          column = index + 1;
          break; // Exit the loop once the correct column is determined
        }
      }
      return {
        ...el,
        column: column
      };
    }));

    // Remove processed elements and mark gaps as seen
    remainingElements = remainingElements.filter(el => !intersectingElements.includes(el));
    intersectingGaps.forEach(gap => seenGaps.add(gap));
  }

  // Add remaining elements as a single column
  if (remainingElements.length) {
    groupedColumns.push(remainingElements.map(el => {
      return {
        ...el,
        column: 1
      };
    }));
  }

  return groupedColumns;
}

function sortColumnElements(groupedColumnElements) {
  /**
   * Sort elements based on their column and position.
   */
  const tolerance = 10;

  return groupedColumnElements.sort((a, b) => {
    const colA = a.column, colB = b.column;
    const yA = Math.round(a.y / tolerance) * tolerance;
    const yB = Math.round(b.y / tolerance) * tolerance;
    const xA = Math.round(a.x / tolerance) * tolerance;
    const xB = Math.round(b.x / tolerance) * tolerance;

    return colA - colB || yA - yB || xA - xB;
  })
}

export function calculateSectionsForWidgets(widgets) {
  let gaps = calculateHorizontalGaps(widgets);
  let gapHeights = gaps.map(gap => gap.height);

  let finalWidgets = [...widgets];

  // Median of horizontal gaps divided by 2 is the cushion distance to obtain a section
  let cushionDistance = median(gapHeights) / 2;
  let sections = [];

  while (widgets.length > 0) {
    // Get the top-most section with all the currently available elements
    let sectionWidgets = _calculateSectionForWidgets(widgets, cushionDistance);
    sections.push(sectionWidgets);

    let sectionWidgetIds = sectionWidgets.map(item => item.id);

    // Update widgets to remove elements in the currently formed section
    widgets = widgets.filter(item => !sectionWidgetIds.includes(item.id));
  }

  const widgetsWithSectionId = addSectionIdToWidgets(finalWidgets, sections);
  return widgetsWithSectionId;
}

function _calculateSectionForWidgets(elements, cushionDistance) {
  // Sort elements by 'y' position
  elements.sort((a, b) => a.y - b.y);

  // Initialize the top-most element and initial vertical range with cushion distance
  const firstElement = elements[0];
  let currentVerticalRange = [
    firstElement.y - cushionDistance,
    firstElement.y + firstElement.height + cushionDistance
  ];

  const section = [];
  const processed = new Set();

  // Keep iterating until all elements in the vertical range are added to the section
  let added;
  do {
    added = false;

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      const [currentLower, currentUpper] = currentVerticalRange;
      const elementLower = element.y - cushionDistance;
      const elementUpper = element.y + element.height + cushionDistance;

      // Check if element intersects the current range and if it's not already processed
      if (!processed.has(element.id) && !(currentUpper < elementLower || currentLower > elementUpper)) {
        // Add intersecting element to section, update vertical range, and mark as processed
        section.push(element);
        processed.add(element.id);
        currentVerticalRange = [
          Math.min(currentLower, elementLower),
          Math.max(currentUpper, elementUpper)
        ];
        added = true;
      }
    }

  } while (added); // Continue only if an element was added in the last iteration

  return section;
}

export function addSectionIdToWidgets(widgets, sections) {
  const updatedWidgets = [...widgets]; // Avoid mutating the original widgets array

  sections.forEach(section => {
    const sectionWidgetIds = section.map(item => item.id); // Collect all section widget IDs once
    const sectionId = `section_${shortid.generate()}`;

    updatedWidgets.forEach(widget => {
      if (sectionWidgetIds.includes(widget.id)) {
        widget.section = sectionId; // Assign sectionId to the widget if its ID matches
      }
    });
  });

  return updatedWidgets;
}

function removeOverlappingWidgets(widgets) {
  /**
   * Removes all elements if their area overlaps with another element more than the threshold.
   * Returns the final list of elements without overlaps and a dictionary of outer elements and the elements it contains inside.
   */
  let overlaps = {};
  let remainingWidgets = [...widgets]; // Create a copy of the elements array

  // Function to calculate the area of an element
  const getArea = (w) => w.width * w.height;

  // Iterate over elements until no overlapping elements are found
  let i = 0;
  while (i < remainingWidgets.length) {
    let elemA = remainingWidgets[i];
    let overlapFound = false;

    remainingWidgets.slice(i + 1).forEach((elemB, j) => {
      // Check if elemA and elemB overlap
      const { isOverlapping, combinedElement } = isOverlap(elemA, elemB, 1);

      if (isOverlapping && !overlapFound && !(elemA.type === GROUP || elemB.type === GROUP)) {
        overlapFound = true;

        let smallerElement = getArea(elemA) < getArea(elemB) ? elemA : elemB;
        let biggerElement = getArea(elemA) >= getArea(elemB) ? elemA : elemB;

        // Track overlapping elements
        overlaps[biggerElement.id] = overlaps[biggerElement.id] || [];
        overlaps[biggerElement.id].push(smallerElement.id);

        // Replace the smaller element with the combined one and remove the smaller element
        remainingWidgets[i + 1 + j] = combinedElement; // Adjust index based on slice
        remainingWidgets.splice(i, 1);
      }
    });

    // Move to the next element only if no overlap was found
    if (!overlapFound) {
      i++;
    }
  }

  return { remainingWidgets, overlaps };
}

function createSectionsForWidgets(widgets, overlaps, context) {

  let sections = [];

  const sectionWidgetsMap = widgets.reduce((acc, widget) => {
    const sectionId = widget.section;

    // If sectionId is not already in the accumulator, initialize it with an empty array
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }

    // Push the widget object into the corresponding sectionId array
    acc[sectionId].push(widget);

    return acc;
  }, {});

  Object.entries(sectionWidgetsMap).forEach(([sectionId, widgets]) => {
    const section = createSectionForWidgets(sectionId, widgets, overlaps, context);
    sections.push(section);
  });

  return sections;
}

function createColumnsForSection(section, overlaps, context) {
  let columns = [];

  const columnWidgetsMap = section.widgets.reduce((acc, widget) => {
    const column = widget.column;

    // If column is not already in the accumulator, initialize it with an empty array
    if (!acc[column]) {
      acc[column] = [];
    }

    // Push the widget object into the corresponding column array
    acc[column].push(widget);

    return acc;
  }, {});

  Object.entries(columnWidgetsMap).forEach(([columnId, widgets]) => {
    const column = createColumnForSection(section, columnId, widgets, overlaps, context);
    columns.push(column);
  })

  section.columns = columns;

  return section;
}

function createColumnForSection(section, columnId, widgets, overlaps, context) {
  var left = Number.MAX_SAFE_INTEGER;
  var top = Number.MAX_SAFE_INTEGER;
  var bottom = 0;
  var right = 0;

  widgets.forEach(widget => {
    left = Math.min(widget.x, left);
    top = Math.min(widget.y, top);

    right = Math.max(right, widget.x + widget.width);
    bottom = Math.max(bottom, widget.y + widget.height);

    let overlappingWidgetIds = overlaps[widget.id] || [];
    if (overlappingWidgetIds && overlappingWidgetIds.length > 0) {
      let overlappingWidgets = overlappingWidgetIds.map(wId => context.widgets.find(w => w.uid === wId));
      let overlappingWidgetRequests = generateWidgetRequestForDesktop(context.editorSession, overlappingWidgets);
      overlappingWidgetRequests.forEach(widget => {
        left = Math.min(widget.x, left);
        top = Math.min(widget.y, top);

        right = Math.max(right, widget.x + widget.width);
        bottom = Math.max(bottom, widget.y + widget.height);
      })
    }
  })

  const width = Math.ceil(right - left);
  const height = Math.ceil(bottom - top);

  return {
    id: columnId,
    type: 'Column',
    section: section.id,
    x: left,
    y: top,
    width: width,
    height: height,
    widgets: widgets
  }
}

function createSectionForWidgets(sectonid, widgets, overlaps, context) {
  var left = Number.MAX_SAFE_INTEGER;
  var top = Number.MAX_SAFE_INTEGER;
  var bottom = 0;
  var right = 0;

  widgets.forEach(widget => {
    left = Math.min(widget.x, left);
    top = Math.min(widget.y, top);

    right = Math.max(right, widget.x + widget.width);
    bottom = Math.max(bottom, widget.y + widget.height);

    let overlappingWidgetIds = overlaps[widget.id] || [];
    if (overlappingWidgetIds && overlappingWidgetIds.length > 0) {
      let overlappingWidgets = overlappingWidgetIds.map(wId => context.widgets.find(w => w.uid === wId));
      let overlappingWidgetRequests = generateWidgetRequestForDesktop(context.editorSession, overlappingWidgets);
      overlappingWidgetRequests.forEach(widget => {
        left = Math.min(widget.x, left);
        top = Math.min(widget.y, top);

        right = Math.max(right, widget.x + widget.width);
        bottom = Math.max(bottom, widget.y + widget.height);
      })
    }
  })

  widgets = sortWidgets(widgets);

  const width = Math.ceil(right - left);
  const height = Math.ceil(bottom - top);

  return {
    id: sectonid,
    type: 'Section',
    x: left,
    y: top,
    width: width,
    height: height,
    widgets: widgets
  }
}

function isOverlap(elemA, elemB, threshold) {
  /**
   * Checks if elemA or elemB overlap each other over the threshold area percentage.
   * If the overlap is more than the threshold, returns true and the combined element.
   * Otherwise, returns false.
   */

  // Calculate the intersection dimensions of the two elements
  const xOverlap = Math.max(0, Math.min(elemA.x + elemA.width, elemB.x + elemB.width) - Math.max(elemA.x, elemB.x));
  const yOverlap = Math.max(0, Math.min(elemA.y + elemA.height, elemB.y + elemB.height) - Math.max(elemA.y, elemB.y));

  // If no overlap, return early
  if (xOverlap === 0 || yOverlap === 0) {
    return { isOverlapping: false, combinedElement: {} };
  }

  // Calculate the intersection area
  const intersectionArea = xOverlap * yOverlap;

  // Calculate the areas of both elements
  const areaA = elemA.width * elemA.height;
  const areaB = elemB.width * elemB.height;
  const smallerArea = Math.min(areaA, areaB);

  // Check if the overlap area exceeds the threshold
  if (intersectionArea / smallerArea >= threshold) {
    // Combine the two elements based on their bounding box
    const combinedX = Math.min(elemA.x, elemB.x);
    const combinedY = Math.min(elemA.y, elemB.y);
    const combinedWidth = Math.max(elemA.x + elemA.width, elemB.x + elemB.width) - combinedX;
    const combinedHeight = Math.max(elemA.y + elemA.height, elemB.y + elemB.height) - combinedY;

    // Use the properties of the bigger element (based on area)
    const biggerElement = areaA >= areaB ? elemA : elemB;

    const combinedElement = {
      id: biggerElement.id,
      type: biggerElement.type,
      section: biggerElement.section,
      x: combinedX,
      y: combinedY,
      rotation: biggerElement.rotation,
      width: combinedWidth,
      height: combinedHeight,
    };

    return { isOverlapping: true, combinedElement };
  }

  return { isOverlapping: false, combinedElement: {} };
}

function median(arr) {
  const sortedArr = arr.slice().sort((a, b) => a - b);  // Sort array in ascending order
  const mid = Math.floor(sortedArr.length / 2);

  if (sortedArr.length % 2 === 0) {
    // If even, return the average of the two middle numbers
    return (sortedArr[mid - 1] + sortedArr[mid]) / 2;
  } else {
    // If odd, return the middle number
    return sortedArr[mid];
  }
}

/**
 * Calculate dimension for a widget
 */
export function recalculateDimensionsForWidget(context, widget) {
  const editorSession = context.editorSession;
  const breakpoint = context.breakpoint;

  var layoutsMap = {}
  context.widgets.forEach(widget => {
    const layout = _.get(widget, `properties.${breakpoint}.layout`);
    layoutsMap[widget.uid] = layout;
  });

  const newContext = {
    ...context,
    layouts: layoutsMap,
    topOffset: 20,
    padding: {
      horizontal: 20,
      vertical: 10
    },
    availableWidth: 375
  }

  _recalculateLayoutForBreakpoint(newContext, widget, 0);

  //Merge the layout maps
  context.layouts = newContext.layouts;

  context.widgets.forEach(widget => {
    var layout = context.layouts[widget.uid];
    const type = widget.type;

    const widgetConfig = WidgetRegistry.moveableConfig(editorSession, type, breakpoint);
    if (widgetConfig && widgetConfig.onResize) {
      layout = widgetConfig.onResize(widget, layout, breakpoint);
    }

    context.layouts[widget.uid] = layout;
  });

  return context.layouts;
}

function _recalculateLayoutForBreakpoint(context, widget, index) {
  const {
    editorSession,
    widgets,
    breakpoint,
    availableWidth,
    padding
  } = context;

  //Create new context
  const newContext = calculateContextForWidget(context, widget);

  if (newContext) {
    var children = widgets.filter(item => item.parent === widget.uid);
    children = sortWidgetsByPosition(editorSession, children);

    children.forEach((child, index) => {
      _recalculateLayoutForBreakpoint(newContext, child, index);
    });

    //Merge the layout maps
    context.layouts = newContext.layouts;
  }

  const layout = _calculateLayoutForWidget(context, widget, index);
  if (layout) {
    context.layouts[widget.uid] = layout;
  }
}

/**
 * Calculate dimension for a widget
 * context: 
 *  - editor Session
 *  - widgets
 *  - Map of calculated widget layouts
 *  - Breakpoint
 *  - Available width
 * widget - widget object
 * index - Position of the widget in the children
*/
export function _calculateLayoutForWidget(context, widget, index) {

  const {
    editorSession,
    layouts,
    availableWidth,
  } = context;

  const layout = layouts[widget.uid];
  var newLayout = new Frame(layout);

  const type = widget.type;
  var rect = null;
  const desktopLayout = _.get(widget, 'desktopLayout', {});

  switch (type) {
    case PAGE:
      rect = calculateDimensionForContainer(context, widget, availableWidth);
      break;
    case SLIDE:
      rect = calculateDimensionForContainer(context, widget, availableWidth);
      break;
    case FORM:
      rect = calculateDimensionForForm(context, widget, availableWidth);
      break;
    case FORM_STEP:
      rect = calculateDimensionForContainer(context, widget, availableWidth);
      break;
    case FORM_FIELD:
      rect = calculateFormInputDimension(editorSession, widget, availableWidth);
      break;
    case SECTION:
      rect = calculateSectionDimension(context, widget, 375);
      break;

    case MENU:
      rect = calculateSectionDimension(context, widget, 375);
      break;

    case CAROUSEL:
      rect = calculateCarouselDimension(context, widget, 375);
      break;

    case CAROUSEL_CARD:
      rect = calculateCarouselCardDimension(context, widget, 375);
      break;

    case SEEK:
      rect = calculateSeekDimension(context, widget, 375);
      break;

    case SECTION_COLUMN:
      rect = calculateSectionColumnDimension(context, widget, 375);
      break;

    case GROUP:
      if(parseInt(desktopLayout?.width) > 335){
        rect = calculateDimensionForForm(context, widget, 335);
        break;
      }
    case CONTENT:
      rect = calculateWidgetDimension(editorSession, widget, 375);
      break;
    case HOTSPOT:
      rect = calculateDimensionForHotspot(context, widget, availableWidth);
      break;
    case ASSET:
      rect = calculateWidgetDimension(editorSession, widget, 375);
      break;

    default:
      rect = calculateWidgetDimension(editorSession, widget, availableWidth);
  }

  if (!rect || rect.width === 0) {
    return newLayout.get();
  }

  newLayout.set('width', `${rect.width}px`);
  newLayout.set('height', `${rect.height}px`);

  return newLayout.get();
}

export function calculateWidgetDimension(editor, widget, availableWidth) {
  const layoutHelper = document.querySelector(`#${editor} .widget_layout_helper`);
  if (!layoutHelper) {
    return null;
  }

  const widgetElement = document.querySelector(`#${editor} .editor_widget_renderer #${widget.uid}`);
  if (!widgetElement) {
    return null;
  }

  const rect = widgetElement.getBoundingClientRect();

  const newWidth = Math.min(availableWidth, rect.width);
  const newHeight = rect.height > 0 ? (rect.height / rect.width) * newWidth : 0;

  const newRect = new DOMRect(rect.x, rect.y, newWidth, newHeight);
  return newRect;
}

function calculateFormInputDimension(editor, widget, availableWidth) {
  const widgetElement = document.querySelector(`#${editor} .editor_widget_renderer #${widget.uid}`);
  if (!widgetElement) {
    return null;
  }

  const rect = widgetElement.getBoundingClientRect();

  const newWidth = Math.min(availableWidth, rect.width);
  const newHeight = rect.height;

  const newRect = new DOMRect(rect.x, rect.y, newWidth, newHeight);
  return newRect;
}


export function calculateDimensionForContainer(context, widget, availableWidth) {
  const {
    editorSession,
    widgets,
    layouts,
    padding
  } = context;

  var containerRect = calculateWidgetDimension(editorSession, widget, availableWidth);
  if (!containerRect) {
    return null;
  }

  var height = 0;
  var children = widgets.filter(item => item.parent === widget.uid);
  children.forEach(child => {
    const layout = layouts[child.uid];
    const rect = getBoundingRectFromLayout(layout);

    if (rect && !isNaN(rect.height)) {
      const offset = rect.y + rect.height;
      height = Math.max(offset, height);
    }
  });

  containerRect.height = height + padding.vertical;
  return containerRect;
}

export function calculateDimensionForCarouselContainer(context, widget, availableWidth) {
  const {
    editorSession,
    widgets,
    layouts,
    padding
  } = context;

  var containerRect = calculateWidgetDimension(editorSession, widget, availableWidth);
  if (!containerRect) {
    return null;
  }

  var height = 0;
  var children = widgets.filter(item => item.parent === widget.uid);

  children.forEach(child => {
    const layout = layouts[child.uid];
    const rect = getBoundingRectFromLayout(layout);
    if (rect && !isNaN(rect.height)) {
      height = Math.max(rect.height, height);
    }
  });

  containerRect.height = height + padding.vertical;
  return containerRect;
}


export function calculateDimensionForHotspot(context, widget, availableWidth) {
  const {
    editorSession,
    widgets,
    layouts,
    padding
  } = context;

  var containerRect = calculateWidgetDimension(editorSession, widget, availableWidth);
  if (!containerRect || containerRect.width === 0) {
    return null;
  }

  var height = 0;
  var children = widgets.filter(item => item.parent === widget.uid);
  children.forEach(child => {
    const layout = layouts[child.uid];
    const rect = getBoundingRectFromLayout(layout);

    if (rect && !isNaN(rect.height)) {
      const offset = rect.y + rect.height;
      height = Math.max(offset, height);
    }
  });

  containerRect.height = height + padding.vertical;
  return containerRect;
}

export function calculateDimensionForForm(context, widget, availableWidth) {

  const {
    editorSession,
    widgets,
    layouts,
    padding
  } = context;

  var containerRect = calculateWidgetDimension(editorSession, widget, availableWidth);
  if (!containerRect) {
    return null;
  }

  var children = widgets.filter(item => item.parent === widget.uid);

  var height = 0;
  children.forEach(child => {
    const layout = layouts[child.uid];
    const rect = getBoundingRectFromLayout(layout);

    if (rect && !isNaN(rect.height)) {
      height = Math.max(rect.height, height);
    }
  });

  containerRect.height = height;
  return containerRect;
}

export function calculateSectionDimension(context, widget, availableWidth) {
  const newContext = calculateContextForWidget(context, widget);
  const containerRect = calculateDimensionForContainer(newContext, widget, availableWidth) || {};

  containerRect.width = 375;

  return containerRect;
}

export function calculateSeekDimension(context, widget, availableWidth) {
  const newContext = calculateContextForWidget(context, widget);
  const containerRect = calculateWidgetDimension(newContext.editorSession, widget, availableWidth);
  containerRect.width = 375;

  return containerRect;
}

export function calculateCarouselCardDimension(context, widget, availableWidth) {
  const newContext = calculateContextForWidget(context, widget);
  const containerRect = calculateDimensionForContainer(newContext, widget, availableWidth) || {};

  containerRect.width = availableWidth;
  return containerRect;
}

let widgetMaxHeights = 0;
let slideIdWithHeights = {};

export function calculateCarouselDimension(context, widget, availableWidth) {
  const newContext = calculateContextForWidget(context, widget);
  const containerRect = calculateDimensionForCarouselContainer(newContext, widget, availableWidth) || {};
  const currentHeight = containerRect.height || 0;
  const visibleSlideId = getCarouselSlideId(context, widget);
  const maxHeightSlideId = getMaxHeightSlideId(slideIdWithHeights);

  if (!visibleSlideId) return containerRect;
  if (maxHeightSlideId === visibleSlideId) {
    widgetMaxHeights = 0;
  }

  widgetMaxHeights = Math.max(widgetMaxHeights || 0, currentHeight, 400);
  slideIdWithHeights[visibleSlideId] = widgetMaxHeights;

  if (maxHeightSlideId !== visibleSlideId) {
    slideIdWithHeights[visibleSlideId] = Math.max(currentHeight, 400);;
  }

  containerRect.width = availableWidth;
  containerRect.height = widgetMaxHeights;

  return containerRect;
}

export function calculateSectionColumnDimension(context, widget, availableWidth) {
  const newContext = calculateContextForWidget(context, widget);
  const containerRect = calculateDimensionForContainer(newContext, widget, availableWidth) || {};
  const height = Math.max(containerRect.height ? containerRect.height : 0, 400);

  containerRect.width = availableWidth;
  containerRect.height = height;
  return containerRect;
}

export function calculateGroupDimension(context, widget, availableWidth) {
  const newContext = calculateContextForWidget(context, widget);
  const containerRect = calculateDimensionForContainer(newContext, widget, availableWidth) || {};
  const height = Math.max(containerRect.height ? containerRect.height : 0, 400);

  containerRect.width = availableWidth;
  containerRect.height = height;
  return containerRect;
}

export function getCarouselSlideId(context, widget) {
  const { widgets } = context;
  const children = widgets.filter(item => item.parent === widget.uid);

  const visibleSlide = children.find(slide => {
    const slideElement = document.getElementById(slide.uid);
    return slideElement?.getAttribute('data-selectable') !== null;
  });

  return visibleSlide ? visibleSlide.uid : null;
}

export function getMaxHeightSlideId(slideIdWithHeights) {
  let maxSlideId = null;
  let maxHeight = 0;

  for (const [slideId, height] of Object.entries(slideIdWithHeights)) {
    if (height > maxHeight) {
      maxHeight = height;
      maxSlideId = slideId;
    }
  }

  return maxSlideId;
}

function calculateContextForWidget(context, widget) {
  const {
    availableWidth,
  } = context;

  const parent = getParentWidget(context, widget);
  const isGroupParent = parent?.type === GROUP;

  var topOffset = isGroupParent? 0 : 20;
  var leftOffset = isGroupParent? 0 : 20;

  var newWidth = availableWidth - 2 * leftOffset;
  const type = widget.type;
  const desktopLayout = _.get(widget, 'desktopLayout', {});
  switch (type) {
    case FORM:
    case EXTERNAL_FORM:
      newWidth = availableWidth;
      topOffset = 20;
      leftOffset = 20;
      break;
    case SECTION:
      newWidth = availableWidth;
      topOffset = 0;
      leftOffset = 0;
      break;
    case GROUP:
      newWidth = availableWidth;
      topOffset = 20;
      leftOffset = 20;
      break;
    case SEEK:
      newWidth = availableWidth;
      topOffset = 20;
      leftOffset = 0;
      break;
    case MENU:
      newWidth = availableWidth;
      topOffset = 0;
      leftOffset = 0;
      break;
    case SECTION_COLUMN:
      newWidth = availableWidth;
      topOffset = 0;
      leftOffset = 0;
      break;
    case CAROUSEL:
      newWidth = availableWidth;
      topOffset = 0;
      leftOffset = 0;
      break;
    case CAROUSEL_CARD:
      newWidth = availableWidth;
      topOffset = 0;
      leftOffset = 0;
      break;
    case ASSET: 
    case FORM_STEP: {
      newWidth = availableWidth;
      topOffset = 0;
      leftOffset = 0;
      break;
    }
    case CONTENT:
    case CONTENT_CARD:
      topOffset = 0;
      leftOffset = 0;
      break;
    case MENU_DRAGGABLE_CONTAINER:
      topOffset = 10;
      leftOffset = availableWidth - (widget.width || 0) - 10;
      break;

    default:
      break;
  }

  const newContext = {
    ...context,
    availableWidth: newWidth,
    topOffset: topOffset,
    leftOffset: leftOffset
  }

  return newContext;
}

function getParentWidget(context, widget) {
  const widgetData = context.widgets.find(w => w.uid === widget.id);
  const parent = context.widgets.find(w => w.uid === _.get(widgetData, `parent`));
  return parent;
}

export function sortWidgetsByPosition(editor, widgets) {
  if (_.size(widgets) == 0) {
    return widgets;
  }

  const result = [...widgets];

  const cache = new Map();

  const getElementRectange = (widget) => {
    const id = widget.uid;
    var rect = cache.get(id);
    if (rect == null) {
      const layout = _.get(widget, `properties.desktop.layout`);
      const positon = new Frame(layout).get("transform", "translate");
      if (positon) {
        const value = positon.split(",").map(str => {
          str = str.trim();
          return parseFloat(str);
        });

        rect = {
          x: value[0],
          y: value[1]
        }

      } else {
        rect = {
          x: 0,
          y: 0
        }
      }

      cache.set(id, rect);
    }

    if (rect == null) {
      return null;
    }

    return rect;
  }

  result.sort((w1, w2) => {
    const r1 = getElementRectange(w1);
    const r2 = getElementRectange(w2);

    if (r1.y == r2.y) {
      return r1.x - r2.x;
    }

    return r1.y - r2.y;
  });

  return result;
}

export function getBoundingRectFromLayout(layout) {
  if (!layout) {
    return null;
  }

  const frame = new Frame(layout);
  const translate = frame.get("transform", "translate") || '0px, 0px';
  const position = translate.split(",").map(str => {
    str = str.trim();
    return parseFloat(str);
  });

  const width = parseFloat(frame.get('width'));
  const height = parseFloat(frame.get('height'));

  return {
    x: position[0],
    y: position[1],
    width: width,
    height: height
  }
}

export function getWidgetBoundingRectForBreakpoint(editor, widget, breakpoint) {
  var widgetElement = getWidgetElementForBreakpoint(editor, widget.uid, breakpoint);

  if (!widgetElement) {
    return null;
  }

  var parentRect = new DOMRect();
  if (widget.parent != null) {
    var parentElement = getWidgetElementForBreakpoint(editor, widget.parent, breakpoint);
    if (parentElement) {
      parentRect = parentElement.getBoundingClientRect();
    }
  }

  const rect = widgetElement.getBoundingClientRect();

  const transform = window.getComputedStyle(widgetElement).transform;

  let rotate = 0;
  if (transform && transform !== "none") { 
    const matrix = transform.match(/matrix\(([^)]+)\)/) || transform.match(/matrix3d\(([^)]+)\)/);

    if (matrix) {
      const values = matrix[1].split(", ").map(parseFloat);

      if (values.length === 6) {
        // 2D Matrix
        const [a, b, c, d] = values;

        rotate = Math.atan2(b, a) * (180 / Math.PI);
      } else if (values.length === 16) {
        const a = values[0];
        const b = values[1];

        rotate = Math.atan2(b, a) * (180 / Math.PI);
      }
    }
  }

  return {
    x: rect.left - parentRect.left,
    y: rect.top - parentRect.top,
    rotate: rotate,
    width: rect.width,
    height: rect.height,
  }
}

export function getWidgetElementForBreakpoint(editor, id, breakpoint) {
  var widgetElement = document.querySelector(`#${editor} .editor_widget_scroll_container.${breakpoint} #${id}`);
  if (!widgetElement) {
    widgetElement = document.querySelector(`#${editor} .editor_device_preview.${breakpoint} #${id}`);
  }

  return widgetElement;
}